/**
 * AddPatient 业务逻辑 Hook - 平台无关，纯逻辑复用
 * 请求通过 props 传入，减少 platform 兼容方法
 */
import type { EditPatientData, OptionItem, ProvinceNode } from './types';
import { useState, useEffect, useCallback } from 'react';
import { ToastService } from '../Toast';
import { DEFAULT_CERT_TYPE, DEFAULT_GENDER, DEFAULT_PATIENT_RELATION } from './constants';
import { formatDate, getAgeByBirthday } from './format';
import { isIdCard, isNumberAndWord, isUsername } from './validate';
export interface UseAddPatientFormProps {
  value?: EditPatientData;
  onChange?: (key: string, value: any, args?: any) => void;
  onSave?: (onSuccess?: () => void) => void;
  onSaveRequest?: (formData: EditPatientData, onSuccess?: () => void) => void | Promise<void>;
  showPatientDistrict?: boolean;
  patientRelationOptions?: OptionItem[];
  certTypeOptions?: OptionItem[];
  genderOptions?: OptionItem[];
  provinceData?: ProvinceNode[];
  /** 请求：获取是否有本人，建议通过 props 传入，不传则默认 false */
  onFetchHasSelf?: () => Promise<boolean>;
  /** 请求：根据姓名获取亲属证件号，建议通过 props 传入，不传则返回空 */
  onFetchPatientRelatives?: (patientName: string) => Promise<{
    relativesCertNo?: string;
    relativesId?: string;
  }>;
  /** 请求：获取省市区数据，provinceData 未传且需要地区时使用 */
  onGetProvinceData?: () => Promise<ProvinceNode[]>;
  onFetchPatientNo?: () => Promise<string>;
  onInitClear?: () => void;
  onInitDefaults?: () => void;
}

const initialFormData: EditPatientData = {
  patientCertType: 'I',
  patientSocialSecurityType: '99',
  patientDistrictName: '北京市-北京市-东城区',
  patientDistrictCode: [110000, 110100, 110101],
  bloodSugarList: [{ bloodSugar: '', highBloodPressure: '', lowBloodPressure: '', heartRate: '', rangeTimeType: '' }],
};

export function useAddPatientForm(props: UseAddPatientFormProps) {
  const {
    value: valueProp,
    onChange,
    onSave,
    onSaveRequest,
    showPatientDistrict = true,
    patientRelationOptions = DEFAULT_PATIENT_RELATION,
    certTypeOptions = DEFAULT_CERT_TYPE,
    genderOptions = DEFAULT_GENDER,
    provinceData: provinceDataProp,
    onFetchHasSelf,
    onFetchPatientRelatives,
    onGetProvinceData,
    onFetchPatientNo,
    onInitClear,
    onInitDefaults,
  } = props;

  const [blurName, setBlurName] = useState('');
  const [idNumberDisabled, setIdNumberDisabled] = useState(false);
  const [selfDisabled, setSelfDisabled] = useState(false);
  const [hasSelf, setHasSelf] = useState(false);
  const [provinceData, setProvinceData] = useState<ProvinceNode[]>(provinceDataProp || []);
  const isControlled = valueProp !== undefined;
  const [internalData, setInternalData] = useState<EditPatientData>(() => ({
    ...initialFormData,
    ...valueProp,
  }));
  const editPatientData = isControlled ? valueProp : internalData;

  const {
    patientRelation = '',
    patientName = '',
    patientCertType = '',
    patientCertNo = '',
    patientGender = '',
    patientDistrictCode = '',
    patientDistrictName = '',
    patientWeight = '',
    patientBirthday = '',
    isRelative = false,
  } = editPatientData;

  // 提前定义 applyChangeRaw，避免在 useEffect 中调用时未定义
  const applyChangeRaw = useCallback(
    (key: string, val: any, args?: any) => {
      if (isControlled && onChange) {
        if (key === 'patientDistrictCode') {
          onChange('patientDistrictName', args);
        }
        onChange(key, val);
      } else {
        setInternalData((prev) => {
          const next = { ...prev, [key]: val };
          if (key === 'patientDistrictCode') {
            next.patientDistrictName = args;
          }
          return next;
        });
      }
    },
    [isControlled, onChange]
  );

  // 初始化 effect：获取 hasSelf 和初始化表单
  useEffect(() => {
    (onFetchHasSelf ?? (() => Promise.resolve(false)))().then((v) => setHasSelf(v));

    if (isControlled) {
      onInitClear?.();
      onInitDefaults?.();
    } else {
      setInternalData((prev) => ({ ...initialFormData, ...prev }));
    }
  }, [isControlled, onFetchHasSelf, onInitClear, onInitDefaults]);

  // 单独的 effect：自动获取 patientNo（仅在组件挂载时执行一次）
  useEffect(() => {
    if (onFetchPatientNo && !editPatientData.patientNo) {
      onFetchPatientNo().then((patientNo) => {
        if (patientNo) {
          // 直接调用更新逻辑，避免依赖 applyChangeRaw 导致循环
          if (isControlled && onChange) {
            onChange('patientNo', patientNo);
          } else {
            setInternalData((prev) => {
              const next = { ...prev, patientNo };
              return next;
            });
          }
        } else {
          console.warn('[AddPatient] patientNo 为空，未更新');
        }
      }).catch((err) => {
        console.error('[AddPatient] 获取 patientNo 失败:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅在挂载时执行一次

  useEffect(() => {
    if (patientCertType === 'I' && patientCertNo) {
      const parsed = isIdCard(patientCertNo);
      if (parsed) {
        applyChangeRaw('patientBirthday', parsed.birthday);
        applyChangeRaw('patientGender', parsed.sex);
        applyChangeRaw('patientSocialSecurityType', '99');
        setIdNumberDisabled(true);
      } else {
        setIdNumberDisabled(false);
        applyChangeRaw('patientBirthday', '');
        applyChangeRaw('patientGender', '');
      }
    } else {
      setIdNumberDisabled(false);
      applyChangeRaw('patientBirthday', '');
      applyChangeRaw('patientGender', '');
    }
  }, [patientCertType, patientCertNo, applyChangeRaw]);

  const patientInfoChange = useCallback(
    (key: string, val: any, args?: any) => {
      if (idNumberDisabled && key === 'patientGender') {
        return;
      }
      if (isControlled && onChange) {
        if (key === 'patientDistrictCode') {
          onChange('patientDistrictName', args);
        }
        onChange(key, val);
      } else {
        setInternalData((prev) => {
          const next = { ...prev, [key]: val };
          if (key === 'patientDistrictCode') {
            next.patientDistrictName = args;
          }
          return next;
        });
      }
    },
    [idNumberDisabled, isControlled, onChange]
  );

  const changePatientWeight = useCallback(
    (val: string) => {
      if (/^(\d+|\d+\.\d{0,2})$/.exec(val) || val === '') {
        patientInfoChange('patientWeight', val);
      }
    },
    [patientInfoChange]
  );

  const validate = useCallback(() => {
    const {
      patientNo = '',
      patientRelation: rel = '',
      patientName: name = '',
      patientCertType: certType = '',
      patientCertNo: certNo = '',
      patientCertNoCOVER: certNoCover = '',
      patientDistrictCode: districtCode = '',
      patientWeight: weight = '',
      patientBirthday: birthday = '',
    } = editPatientData;

    const isFill = isRelative && certNoCover === certNo;
    const age = Number(birthday ? getAgeByBirthday(formatDate(birthday, 'yyyy-MM-dd')) : '');

    if (!isFill) {
      patientInfoChange('relativesId', '');
    }

    if (patientNo === '') {
      ToastService.warning('添加患者异常，请重新添加患者');
      return false;
    }
    if (rel === '') {
      ToastService.warning('您还未选择患者于您的关系');
      return false;
    }
    if (name.trim() === '') {
      ToastService.warning('您还未输入患者的姓名');
      return false;
    }
    if (!isUsername(name)) {
      ToastService.warning('您输入的患者姓名格式错误');
      return false;
    }
    if (certNo.trim() === '') {
      ToastService.warning('您还未输入患者的证件号码');
      return false;
    }
    if (certType === 'I' && !isIdCard(certNo) && !isFill) {
      ToastService.warning('您输入的患者证件号码格式错误');
      return false;
    }
    if (certType !== 'I' && !isNumberAndWord(certNo) && !isFill) {
      ToastService.warning('您输入的患者证件号码格式错误');
      return false;
    }
    if (certType !== 'I' && !patientGender.trim()) {
      ToastService.warning('您还未选择患者性别');
      return false;
    }
    if (certType !== 'I' && !birthday.trim()) {
      ToastService.warning('您还未输入患者出生日期');
      return false;
    }
    if (weight === '' && age >= 0 && age <= 12 && !isFill) {
      ToastService.warning('您还未输入患者体重');
      return false;
    }
    if (Number(weight) > 1000) {
      ToastService.warning('患者体重最大为1000KG');
      return false;
    }
    if (showPatientDistrict && !districtCode) {
      ToastService.warning('您还未选择患者的所在地区');
      return false;
    }
    return true;
  }, [editPatientData, isRelative, patientGender, showPatientDistrict, patientInfoChange]);

  const savePatientClick = useCallback(
    (onSavedSuccess?: () => void) => {
      if (!validate()) {
        return;
      }
      if (onSave) {
        onSave(onSavedSuccess);
      } else if (onSaveRequest) {
        // 转换数据格式：将 patientDistrictCode 数组转为字符串
        const submitData = {
          ...editPatientData,
          patientDistrictCode: Array.isArray(editPatientData.patientDistrictCode)
            ? editPatientData.patientDistrictCode.join('-')
            : editPatientData.patientDistrictCode,
        };
        // onSaveRequest 内部会在成功时调用 onSavedSuccess，无需在外部再次调用
        // 移除 Promise.then 中对 onSavedSuccess 的调用，避免在接口失败时仍然触发成功回调
        onSaveRequest(submitData, onSavedSuccess);
      } else {
        onSavedSuccess?.();
      }
    },
    [validate, onSave, onSaveRequest, editPatientData]
  );

  const nameBlur = useCallback(() => {
    if (patientName === blurName) {
      return;
    }
    setBlurName(patientName);
    (onFetchPatientRelatives ?? (() => Promise.resolve({})))(patientName).then((res: { relativesCertNo?: string; relativesId?: string }) => {
      if (res?.relativesCertNo) {
        patientInfoChange('isRelative', true);
        patientInfoChange('patientCertNo', res.relativesCertNo);
        patientInfoChange('patientCertNoCOVER', res.relativesCertNo);
        patientInfoChange('relativesId', res?.relativesId ?? '');
      } else {
        patientInfoChange('isRelative', false);
        patientInfoChange('relativesId', '');
      }
    });
  }, [patientName, blurName, onFetchPatientRelatives, patientInfoChange]);

  useEffect(() => {
    if (provinceDataProp && provinceDataProp.length > 0) {
      setProvinceData(provinceDataProp);
    } else if (!provinceDataProp && showPatientDistrict && onGetProvinceData) {
      onGetProvinceData().then(setProvinceData);
    }
  }, [provinceDataProp, showPatientDistrict, onGetProvinceData]);

  const districtValue = Array.isArray(patientDistrictCode)
    ? patientDistrictCode
    : patientDistrictCode
      ? String(patientDistrictCode).split('-').map(Number)
      : undefined;

  return {
    editPatientData,
    patientRelation,
    patientName,
    patientCertType,
    patientCertNo,
    patientGender,
    patientDistrictCode,
    patientDistrictName,
    patientWeight,
    patientBirthday,
    patientRelationOptions,
    certTypeOptions,
    genderOptions,
    provinceData,
    districtValue,
    hasSelf,
    selfDisabled,
    idNumberDisabled,
    showPatientDistrict,
    patientInfoChange,
    changePatientWeight,
    savePatientClick,
    nameBlur,
  };
}
